import { useEffect, useRef } from 'react';
import { useRouter } from '@tanstack/react-router';

// Disable history-based enhanced page views in the GA4 stream: this
// component owns initial and client navigation page_view events.
export function GoogleAnalytics({ measurementId }: { measurementId: string }) {
  const router = useRouter();
  const previous = useRef('');
  useEffect(() => {
    if (!/^G-[A-Z0-9]+$/.test(measurementId)) return;
    const target = window as typeof window & {
      dataLayer?: unknown[];
      gtag?: (...args: unknown[]) => void;
    };
    target.dataLayer ||= [];
    if (!document.getElementById('ga-loader')) {
      target.gtag = function () {
        target.dataLayer!.push(arguments);
      };
      target.gtag('js', new Date());
      target.gtag('config', measurementId, { send_page_view: false });
      const script = document.createElement('script');
      script.id = 'ga-loader';
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(script);
    }
    let frame = 0;
    const track = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const url = window.location.href;
        // Planner selection changes do not represent another page visit.
        const pageKey = new URL(url).pathname;
        if (previous.current !== pageKey) {
          target.gtag?.('event', 'page_view', {
            page_location: url,
            page_title: document.title,
          });
          previous.current = pageKey;
        }
      });
    };
    const unsubscribe = router.subscribe('onResolved', track);
    track();
    return () => {
      unsubscribe();
      cancelAnimationFrame(frame);
    };
  }, [measurementId, router]);
  return null;
}
